import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import CreateTicket from '../components/Support/CreateTicket';
import SupportTickets from '../components/Support/SupportTickets';
import PublicFooter from '../components/Layout/PublicFooter';

const Container = styled.div`
  height: 100%;
  display: flex;
  background: ${props => props.theme.background};
  overflow: hidden;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 0.75rem;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.backgroundSecondary};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.buttonPrimary};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.buttonPrimary};
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const SupportHeader = styled.div`
  padding: 0.75rem;
  background: ${props => props.theme.background};

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const SupportTitle = styled.h1`
  color: ${props => props.theme.text};
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 0;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const ToggleButton = styled.button`
  background: ${props => props.active ? props.theme.buttonPrimary : props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  color: ${props => props.theme.text};
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? props.theme.buttonPrimary : props.theme.cardBackgroundHover};
  }
`;

const Content = styled.div`
  flex: 1;
  overflow: auto;
  padding: 0.75rem 0;
  background: ${props => props.theme.background};

  @media (max-width: 768px) {
    padding: 0.5rem 0;
  }
`;

const Support = () => {
  const { user } = useAuth();
  const { settings } = useData();
  const [activeTab, setActiveTab] = useState('create');

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'support') {
      setActiveTab('admin-tickets');
    }
  }, [user]);

  const isPremium = (user?.subscription && user?.subscription !== 'free') || (user?.aiTier && user?.aiTier !== 'free');
  const isCompact = isPremium && settings?.display?.compactMode;

  const getTabs = () => {
    const tabs = [
      { id: 'create', label: 'Create Ticket', component: CreateTicket }
    ];

    if (user?.role === 'admin' || user?.role === 'support') {
      tabs.push(
        { id: 'admin-tickets', label: 'All Tickets', component: () => <SupportTickets isAdmin={true} /> }
      );
    }

    tabs.push(
      { id: 'my-tickets', label: 'My Tickets', component: () => <SupportTickets isAdmin={false} /> }
    );

    return tabs;
  };

  const tabs = getTabs();
  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  return (
    <Container>
      <MainContent>
        <SupportHeader>
          <HeaderTop>
            <SupportTitle compact={isCompact}>Support Center</SupportTitle>
            <HeaderActions>
              {tabs.map(tab => (
                <ToggleButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </ToggleButton>
              ))}
            </HeaderActions>
          </HeaderTop>
        </SupportHeader>

        <Content padding={isCompact ? '0.125rem' : '1.5rem'}>
          {ActiveComponent && <ActiveComponent />}
        </Content>
        <PublicFooter />
      </MainContent>
    </Container>
  );
};

export default Support;